<script setup lang="ts">
import PartTitleHead from '@parts/part-title-head.vue';
import PartLabel from '@parts/part-label.vue';
import PartProgressBar from '@parts/part-progress-bar.vue';
import CoreIcon from '@core/core-icon.vue';
import PartFooterNavigation from '@parts/part-footer-navigation.vue';
import ButtonPrimary from '@buttons/button-primary.vue';
import CoreLoader from '@core/core-loader.vue';
import Popup from '@components/popup/popup.vue';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import { useBytes } from '@composables/bytes';
import { useDates } from '@composables/dates';
import { useCurrency } from '@composables/currency';
import { useReserveStore } from '@store/reserve';
import router from '@/router';

/**
 * «Резервное подключение»: потрачено из выданного, разбивка на бесплатные и
 * оплаченные, дата обновления и вход в покупку ГБ.
 *
 * Правила начисления и хранения не на экране, а в справке «Как это работает»
 * (попап): на самом экране — только цифры и одна подсказка, зачем резерв.
 *
 * Второй подписочной ссылки здесь нет и быть не должно: форк страницы подписки
 * отдаёт резервные серверы по той же ссылке, что уже есть у пользователя.
 *
 * Сама покупка живёт на странице оплаты, вкладка «Гигабайты»: платёжный поток у
 * пакета и подписки один и тот же.
 */
const { t, locale } = useI18n();
const route = useRoute();
const toast = useToast();
const { formatGb, formatTraffic, formatTrafficPair } = useBytes();
const { formatDate, daysUntil } = useDates();
const { getDisplayCurrency } = useCurrency();
const reserve = useReserveStore();

const isLoading = ref<boolean>(true);
const helpPopup = ref();

// Оранжевым — только когда конец близко, как в карточке
const ALARM_DAYS = 7;

// Кнопка ведёт на вкладку с пакетами — она появляется там только если в валюте
// витрины пакеты вообще есть, поэтому условие здесь то же самое.
const hasPacks = computed((): boolean => {
	return reserve.packs.some(pack => pack.cost.currency === getDisplayCurrency(locale.value));
});

// «Потрачено 16 из 110 ГБ»: считаем от выданного, чтобы сходиться с полосой
const usedText = computed((): string => {
	return t('reserve.used', {
		used: formatTrafficPair(reserve.grantedBytes - reserve.remainingBytes, reserve.grantedBytes)
	});
});

// Без подписки: «Сохранено 90 ГБ» и через сколько дней сгорит
const savedText = computed((): string => {
	return t('reserve.saved', { n: formatTraffic(reserve.savedBytes) });
});

const graceDays = computed((): number | null => daysUntil(reserve.graceUntil));

// Плашка «сгорят через N дней» — только пока есть что хранить и известна дата
const showBurn = computed((): boolean => {
	return reserve.isInactive && reserve.savedBytes > 0 && graceDays.value !== null;
});

// «Обновление бесплатных — 8 октября» или «Закончится 24 сентября»
const nextText = computed((): string => {
	const event = reserve.nextEvent;
	if (!event || reserve.isInactive) return '';
	const date = formatDate(event.at, 'dayMonth');
	if (!date) return '';
	return t(event.kind === 'ends' ? 'reserve.ends_on' : 'reserve.renews_free', { date });
});

const isEndingSoon = computed((): boolean => {
	const event = reserve.nextEvent;
	if (!event || event.kind !== 'ends') return false;
	const days = daysUntil(event.at);
	return days !== null && days <= ALARM_DAYS;
});

const isAlarming = computed((): boolean => reserve.isInactive || reserve.isExhausted);

const goToPacks = () => {
	router.push({ name: 'Payment', query: { tab: 'packs' } });
};

/**
 * Назад — именно назад: на страницу резерва приходят и с главной, и из кабинета,
 * и возврат всегда в кабинет ломал бы путь пользователя. Кабинет остаётся
 * запасным вариантом, когда истории нет: прямая ссылка, открытая в новой вкладке.
 */
const goBack = () => {
	if (window.history.length > 1) {
		router.back();
		return;
	}
	router.push({ name: 'Account' });
};

onBeforeMount(async () => {
	isLoading.value = true;
	await Promise.all([reserve.load(), reserve.loadPacks()]);
	isLoading.value = false;

	// Возврат из чекаута после оплаты пакета. Отметку сразу убираем из адреса,
	// иначе обновление страницы повторит поздравление.
	if (route.query.paid) {
		toast.success(t('reserve.paid'), { position: 'top' });
		void router.replace({ name: 'AccountReserve' });
	}

	// Прямой заход по ссылке без резерва: показывать пустую страницу незачем
	if (!reserve.isVisible) {
		await router.replace({ name: 'Account' });
	}
});
</script>

<template>
	<section class="section-reserve">
		<div class="wrapper section-reserve__wrapper">
			<transition appear name="fade" style="--animation-delay: 0.1s">
				<part-title-head
					:title="t('reserve.title')"
					class="section-reserve__content"
				/>
			</transition>

			<template v-if="isLoading">
				<transition appear name="fade" style="--animation-delay: 0.3s">
					<core-loader :title="t('reserve.loader')" class="section-reserve__loader" />
				</transition>
			</template>

			<template v-else-if="reserve.isVisible">
				<transition appear name="fade" style="--animation-delay: 0.3s">
					<div class="section-reserve__status">
						<part-label
							:pink="!isAlarming"
							:red="isAlarming"
							rounded
							class="section-reserve__label"
							:icon="reserve.isInactive
								? { name: 'danger-circle', viewBox: '0 0 20 20' }
								: reserve.isExhausted
									? { name: 'rotateRight', viewBox: '0 0 19 19' }
									: { name: 'checked', viewBox: '0 0 18 18' }"
						>
							<template v-if="reserve.isInactive">{{ t('reserve.state_inactive') }}</template>
							<template v-else-if="reserve.isExhausted">{{ t('reserve.limited') }}</template>
							<template v-else>{{ t('reserve.state_active') }}</template>
						</part-label>

						<!-- Без подписки: что сохранено из оплаченного и когда сгорит -->
						<template v-if="reserve.isInactive">
							<!-- После сгорания хранить нечего: заголовок с нулём только сбивает -->
							<p
								v-if="!reserve.isBurned"
								class="section-reserve__remaining section-reserve__remaining_alone font-24-pro-bold"
							>
								{{ savedText }}
							</p>
							<ul class="section-reserve__breakdown">
								<li
									class="section-reserve__breakdown-item"
									:class="{ 'section-reserve__breakdown-item_empty': !(reserve.remaining?.free_remaining_bytes) }"
								>
									<core-icon
										name="gift"
										viewBox="0 0 18 18"
										class="section-reserve__breakdown-icon"
									/>
									<span class="section-reserve__breakdown-name">{{ t('reserve.free') }}</span>
									<span class="section-reserve__breakdown-value">
										{{ t('reserve.gb', { n: formatGb(reserve.remaining?.free_remaining_bytes) }) }}
									</span>
								</li>
								<li
									class="section-reserve__breakdown-item"
									:class="{ 'section-reserve__breakdown-item_empty': !reserve.savedBytes }"
								>
									<core-icon
										name="creditCard"
										viewBox="0 0 24 24"
										class="section-reserve__breakdown-icon"
									/>
									<span class="section-reserve__breakdown-name">{{ t('reserve.purchased') }}</span>
									<span class="section-reserve__breakdown-value">
										{{ t('reserve.gb', { n: formatGb(reserve.savedBytes) }) }}
									</span>
								</li>
							</ul>
						</template>

						<template v-else-if="reserve.remaining">
							<p class="section-reserve__remaining font-24-pro-bold">
								{{ usedText }}
							</p>
							<part-progress-bar
								:percent="reserve.usedPercent"
								:label="usedText"
								class="section-reserve__progress"
							/>
							<ul class="section-reserve__breakdown">
								<li class="section-reserve__breakdown-item">
									<core-icon
										name="gift"
										viewBox="0 0 18 18"
										class="section-reserve__breakdown-icon"
									/>
									<span class="section-reserve__breakdown-name">{{ t('reserve.free') }}</span>
									<span class="section-reserve__breakdown-value">
										{{ t('reserve.gb', { n: formatGb(reserve.remaining.free_remaining_bytes) }) }}
									</span>
								</li>
								<li class="section-reserve__breakdown-item">
									<core-icon
										name="creditCard"
										viewBox="0 0 24 24"
										class="section-reserve__breakdown-icon"
									/>
									<span class="section-reserve__breakdown-name">{{ t('reserve.purchased') }}</span>
									<span class="section-reserve__breakdown-value">
										{{ t('reserve.gb', { n: formatGb(reserve.remaining.purchased_remaining_bytes) }) }}
									</span>
								</li>
							</ul>
						</template>

						<p
							v-if="nextText"
							class="section-reserve__note"
							:class="{ 'section-reserve__note_alarming': isEndingSoon }"
						>
							{{ nextText }}
						</p>
						<template v-if="!reserve.isInactive">
							<p
								v-if="!reserve.hasWhitelistData"
								class="section-reserve__note"
							>
								{{ t('reserve.no_data') }}
							</p>
							<p
								v-else-if="reserve.isApproximate"
								class="section-reserve__note"
							>
								{{ t('reserve.approximate') }}
							</p>
						</template>

						<!-- Резерв кончился, но VPN работает: основные серверы безлимитные -->
						<p
							v-if="reserve.isExhausted && !reserve.isInactive"
							class="section-reserve__main-ok"
						>
							<core-icon
								name="checked"
								viewBox="0 0 18 18"
								class="section-reserve__main-ok-icon"
							/>
							<span>{{ t('reserve.main_ok') }}</span>
						</p>
					</div>
				</transition>

				<!-- Оплаченные ещё хранятся: сколько дней осталось и что делать -->
				<transition v-if="showBurn" appear name="fade" style="--animation-delay: 0.35s">
					<div class="section-reserve__burn">
						<core-icon
							name="clock"
							viewBox="0 0 17 16"
							class="section-reserve__burn-icon"
						/>
						<div class="section-reserve__burn-body">
							<p class="section-reserve__burn-title">
								{{ t('reserve.burn_title') }} {{ t('reserve.days', { n: graceDays }, graceDays) }}
							</p>
							<p class="section-reserve__burn-text">{{ t('reserve.burn_text') }}</p>
						</div>
					</div>
				</transition>

				<transition appear name="fade" style="--animation-delay: 0.4s">
					<div class="section-reserve__about">
						<!-- Пока остаток хранится, всё сказано в плашке выше -->
						<p
							v-if="!showBurn"
							class="section-reserve__hint"
						>
							{{ reserve.isInactive ? t('reserve.hint_inactive') : t('reserve.hint') }}
						</p>
						<button
							type="button"
							class="section-reserve__help-link"
							@click="helpPopup?.open()"
						>
							{{ t('reserve.help.link') }}
						</button>
					</div>
				</transition>

				<transition v-if="!reserve.isInactive && hasPacks && !reserve.canBuyPack" appear name="fade" style="--animation-delay: 0.5s">
					<p class="section-reserve__hint section-reserve__hint_disabled">
						{{ t('reserve.buy_disabled') }}
					</p>
				</transition>

				<transition appear name="fade" style="--animation-delay: 0.7s">
					<part-footer-navigation
						:show-back-button="hasPacks || reserve.isInactive"
						disable-back-button-action
						@back-button="goBack"
						class="section-reserve__footer"
					>
						<button-primary
							v-if="reserve.isInactive"
							to="/payment"
							:icon="{ name: 'arrowRight', viewBox: '0 0 40 40' }"
						>
							{{ t('reserve.renew') }}
						</button-primary>
						<button-primary
							v-else-if="hasPacks"
							:disabled="!reserve.canBuyPack"
							:icon="{ name: 'arrowRight', viewBox: '0 0 40 40' }"
							@click="goToPacks"
						>
							{{ t('reserve.buy') }}
						</button-primary>
						<button-primary
							v-else
							pink
							@click="goBack"
						>
							{{ t('common.buttons.back') }}
						</button-primary>
					</part-footer-navigation>
				</transition>
			</template>
		</div>

		<!-- Справка: правила начисления и хранения, чтобы не грузить ими экран -->
		<popup ref="helpPopup">
			<div class="popup__text section-reserve__help">
				<h3 class="popup__title section-reserve__help-title">{{ t('reserve.help.title') }}</h3>
				<p class="section-reserve__help-intro">{{ t('reserve.help.intro') }}</p>
				<hr class="section-reserve__help-divider">
				<p
					v-if="reserve.monthlyGb"
					class="section-reserve__help-item"
				>
					<b>{{ t('reserve.free') }}</b> — {{ t('reserve.help.free', { n: reserve.monthlyGb }) }}
				</p>
				<p class="section-reserve__help-item">
					<b>{{ t('reserve.purchased') }}</b> — {{ t('reserve.help.paid') }}
				</p>
				<p class="section-reserve__help-item">{{ t('reserve.help.grace') }}</p>
			</div>
		</popup>
	</section>
</template>

<style lang="scss">
.section-reserve {
	padding: 0 0 1.6rem;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;

	&__wrapper {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
	}

	&__content {
		margin-bottom: 2.4rem;

		@include min-desktop {
			margin-bottom: 3.2rem;
		}
	}

	&__loader {
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 6.4rem;
		}
	}

	&__status {
		background: var(--color-panel);
		border-radius: 2.4rem;
		padding: 2.4rem 1.6rem;
		text-align: center;
		margin-bottom: 1.6rem;

		@include min-desktop {
			padding: 3.2rem 2.4rem;
			margin-bottom: 2.4rem;
		}
	}

	&__label {
		margin: 0 auto 1.6rem;
		padding-left: 1.6rem;
		padding-right: 1.6rem;
	}

	&__remaining {
		margin-bottom: 1.2rem;

		@include max-desktop {
			font-size: 2.1rem;
		}

		// Без полосы под цифрой — отступ до строк чуть больше
		&_alone {
			margin-bottom: 1.6rem;
		}
	}

	&__progress {
		height: 0.6rem;
		margin-bottom: 1.6rem;
	}

	&__breakdown {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 0.8rem;
		text-align: left;
	}

	&__breakdown-item {
		display: flex;
		align-items: center;
		gap: 1rem;
		min-height: 5.2rem;
		padding: 0.8rem 1.2rem;
		border-radius: 1.2rem;
		background: var(--color-white);

		@include theme-dark {
			background: rgba(var(--color-active-rgb), 0.06);
		}
	}

	// Строка с нулём приглушена: без подписки бесплатных нет
	&__breakdown-item_empty {
		opacity: 0.5;
	}

	&__breakdown-icon {
		flex: 0 0 auto;
		width: 1.8rem;
		height: 1.8rem;
		fill: var(--color-purple);
	}

	&__breakdown-name {
		flex: 1 1 auto;
		min-width: 0;
		@include font-regular;
	}

	&__breakdown-value {
		@include font-16-pro-medium;
		flex: 0 0 auto;
	}

	&__note {
		@include font-12-pro-regular;
		opacity: 0.5;
		margin-top: 1.2rem;

		@include min-desktop {
			@include font-16-pro-regular;
		}

		&_alarming {
			opacity: 1;
			color: var(--color-orange);

			@include theme-dark {
				color: #FFAD9B;
			}
		}
	}

	// Пометка «основное подключение работает»: спокойная, не предупреждение
	&__main-ok {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.8rem;
		margin-top: 1.6rem;
		padding: 1rem 1.2rem;
		border-radius: 1.2rem;
		background: rgba(var(--color-active-rgb), 0.08);
		color: var(--color-active);
		@include font-12-pro-regular;
		line-height: 1.3;
		text-align: left;

		@include min-desktop {
			@include font-16-pro-regular;
		}
	}

	&__main-ok-icon {
		flex: 0 0 auto;
		width: 1.6rem;
		height: 1.6rem;
		fill: currentColor;
	}

	// Плашка «сгорят через N дней»: заголовок оранжевым, пояснение обычным
	// цветом, фон оранжевый на 8 %
	&__burn {
		display: flex;
		align-items: flex-start;
		gap: 1.2rem;
		padding: 1.6rem;
		border-radius: 1.6rem;
		background: rgba(255, 91, 55, 0.08);
		text-align: left;
		margin-bottom: 1.6rem;

		@include min-desktop {
			margin-bottom: 2.4rem;
		}
	}

	&__burn-icon {
		flex: 0 0 auto;
		width: 1.4rem;
		height: 1.4rem;
		margin-top: 0.2rem;
		fill: var(--color-orange);

		@include theme-dark {
			fill: #FFAD9B;
		}
	}

	&__burn-body {
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
		min-width: 0;
	}

	&__burn-title {
		@include font-regular;
		font-weight: 600;
		color: var(--color-orange);

		@include theme-dark {
			color: #FFAD9B;
		}
	}

	&__burn-text {
		@include font-regular;
		line-height: 1.45;
	}

	// Подсказка и ссылка на справку под карточкой
	&__about {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1.2rem;
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 4.8rem;
		}
	}

	&__hint {
		@include font-regular;
		line-height: 1.4;
		text-align: center;
		opacity: 0.6;

		&_disabled {
			@include font-12-pro-regular;
			opacity: 0.8;
			margin: -1.6rem 0 3.2rem;

			@include min-desktop {
				@include font-16-pro-regular;
				margin: -3.2rem 0 4.8rem;
			}
		}
	}

	// Ссылка-кнопка с пунктирным подчёркиванием: открывает попап, а не страницу
	&__help-link {
		border: none;
		background: none;
		padding: 0;
		cursor: pointer;
		@include font-regular;
		color: var(--color-purple);
		text-decoration: underline dashed;
		text-underline-offset: 0.4rem;
		transition: opacity 0.2s ease;

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				opacity: 0.7;
			}
		}
	}

	// Справка в попапе: текст слева, термины жирным
	&__help {
		align-items: stretch;
		text-align: left;
		gap: 1.2rem;
	}

	&__help-title {
		text-align: left;
	}

	&__help-intro,
	&__help-item {
		@include font-regular;
		line-height: 1.45;
		color: var(--color-text);
		margin: 0;

		b {
			font-weight: 600;
		}
	}

	&__help-divider {
		border: none;
		border-top: 1px solid rgba(var(--color-text-rgb), 0.1);
		margin: 0.4rem 0;
	}

	&__footer {
		margin-top: auto;
	}
}
</style>
